import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import { buildApiUrl } from '../config/environment';
import LinkTransactionModal from './LinkTransactionModal';
import { usePermissions } from '../hooks/usePermissions';

const PaymentModal = ({ show, onClose, payment, tenancyLedgerId, property, onSave }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  const [formData, setFormData] = useState({
    id: null,
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    reference: ''
  });
  const [errors, setErrors] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);
  const [isPaymentSaved, setIsPaymentSaved] = useState(false);
  const [originalPaymentData, setOriginalPaymentData] = useState(null);
  const [detailedPayment, setDetailedPayment] = useState(null);
  const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);

  // Link Transaction Modal state
  const [showLinkTransactionModal, setShowLinkTransactionModal] = useState(false);

  // Document upload state
  const [paymentFiles, setPaymentFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedPreviewFile, setSelectedPreviewFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // Files selected but not yet uploaded
  const [fileTypeSelection, setFileTypeSelection] = useState('receipt'); // Default file type
  const [showUploadSection, setShowUploadSection] = useState(false); // Upload section visibility
  const fileInputRef = useRef(null);

  // Fetch payment files when editing an existing payment
  const fetchPaymentFiles = useCallback(async (paymentId) => {
    if (!paymentId) return;

    setLoadingFiles(true);
    try {
      const response = await fetch(buildApiUrl(`/payments/${paymentId}/files`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment files');
      }

      const files = await response.json();
      const filesArray = Array.isArray(files) ? files : [];
      setPaymentFiles(filesArray);

      // Auto-select first file for preview if files exist and nothing is selected
      // Use a callback to get the current state to avoid adding selectedPreviewFile to dependencies
      setSelectedPreviewFile(current => {
        if (!current && filesArray.length > 0) {
          return filesArray[0];
        }
        return current;
      });
    } catch (error) {
      console.error('Error fetching payment files:', error);
      setPaymentFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  // Fetch detailed payment data when editing an existing payment
  useEffect(() => {
    const fetchPaymentDetails = async (paymentId) => {
      setLoadingPaymentDetails(true);
      try {
        const response = await fetch(buildApiUrl(`/payments/${paymentId}`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch payment details');
        }

        const paymentDetails = await response.json();
        setDetailedPayment(paymentDetails);
      } catch (error) {
        console.error('Error fetching payment details:', error);
        setDetailedPayment(null);
      } finally {
        setLoadingPaymentDetails(false);
      }
    };

    if (payment && (payment.id || payment.ID)) {
      const paymentId = payment.id || payment.ID;

      // Always fetch detailed payment data for existing payments
      fetchPaymentDetails(paymentId);

      // Fetch existing files for this payment
      fetchPaymentFiles(paymentId);

      // Parse amount correctly from the API response structure
      let amountValue = '';
      if (payment.amount) {
        if (typeof payment.amount === 'object') {
          // New API format: { amount: 100000, currency: "PHP", formatted: "₱1000.00" }
          if (payment.amount.amount) {
            amountValue = (payment.amount.amount / 100).toString(); // Convert cents to dollars
          }
        } else if (typeof payment.amount === 'number') {
          // Direct number format
          amountValue = (payment.amount / 100).toString(); // Convert cents to dollars
        } else if (typeof payment.amount === 'string') {
          // String format
          amountValue = payment.amount;
        }
      }
      // Fallback to old format if new format not available
      else if (payment.Amount?.amount) {
        amountValue = (payment.Amount.amount / 100).toString();
      }

      const paymentFormData = {
        id: paymentId,
        amount: amountValue,
        payment_date: payment.payment_date
          ? payment.payment_date.split('T')[0]
          : (payment.PaymentDate ? payment.PaymentDate.split('T')[0] : new Date().toISOString().split('T')[0]),
        reference: payment.reference !== undefined ? payment.reference : (payment.Reference || '')
      };
      setFormData(paymentFormData);
      setOriginalPaymentData(paymentFormData); // Store original data for comparison
      setIsPaymentSaved(true); // Payment exists, so it's already saved
    } else {
      const newPaymentData = {
        id: null,
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        reference: ''
      };
      setFormData(newPaymentData);
      setOriginalPaymentData(null);
      setDetailedPayment(null);
      setPaymentFiles([]);
      setPendingFiles([]);
      setIsPaymentSaved(false); // New payment, not saved yet
    }
  }, [payment, fetchPaymentFiles]);

  // Update form data when detailed payment is fetched from API
  useEffect(() => {
    if (detailedPayment && (payment?.id || payment?.ID)) {
      // Parse amount from the detailed payment response
      let amountValue = '';
      if (detailedPayment.amount) {
        if (typeof detailedPayment.amount === 'object') {
          // New API format: { amount: 100000, currency: "PHP", formatted: "₱1000.00" }
          if (detailedPayment.amount.amount) {
            amountValue = (detailedPayment.amount.amount / 100).toString(); // Convert cents to dollars
          }
        } else if (typeof detailedPayment.amount === 'number') {
          // Direct number format
          amountValue = (detailedPayment.amount / 100).toString(); // Convert cents to dollars
        } else if (typeof detailedPayment.amount === 'string') {
          // String format
          amountValue = detailedPayment.amount;
        }
      }
      // Fallback to old format if new format not available
      else if (detailedPayment.Amount?.amount) {
        amountValue = (detailedPayment.Amount.amount / 100).toString();
      }

      // Update form data with detailed payment information
      const updatedFormData = {
        id: detailedPayment.id || detailedPayment.ID,
        amount: amountValue,
        payment_date: detailedPayment.payment_date
          ? detailedPayment.payment_date.split('T')[0]
          : (detailedPayment.PaymentDate ? detailedPayment.PaymentDate.split('T')[0] : formData.payment_date),
        reference: detailedPayment.reference !== undefined ? detailedPayment.reference : (detailedPayment.Reference || '')
      };

      setFormData(updatedFormData);
      setOriginalPaymentData(updatedFormData);
    }
  }, [detailedPayment, payment?.id, payment?.ID]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setPendingFiles([]);
      setSelectedPreviewFile(null);
      setIsDragging(false);
      setShowUploadSection(false);
    }
  }, [show]);

  // Handler to refresh payment details after linking transaction
  const handleTransactionLinked = async () => {
    if (!formData.id) return;

    try {
      const refreshResponse = await fetch(buildApiUrl(`/payments/${formData.id}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });

      if (refreshResponse.ok) {
        const updatedPaymentDetails = await refreshResponse.json();
        setDetailedPayment(updatedPaymentDetails);
      }
    } catch (error) {
      console.error('Error refreshing payment details:', error);
    }
  };

  // Handler to verify payment
  const handleVerifyPayment = async () => {
    if (!formData.id) {
      alert('Please save the payment first before verifying.');
      return;
    }

    const confirmMsg = "Verifying this payment will lock it from further editing. Do you wish to proceed?";
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/payments/${formData.id}/verify`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
        // Empty body - user ID extracted from JWT token server-side
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to verify payment');
      }

      // Refresh payment details to get updated verification status
      const refreshResponse = await fetch(buildApiUrl(`/payments/${formData.id}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });

      if (refreshResponse.ok) {
        const updatedPaymentDetails = await refreshResponse.json();
        setDetailedPayment(updatedPaymentDetails);
      }

      alert('Payment verified successfully!');
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert(`Failed to verify payment: ${error.message}`);
    }
  };

  const createPayment = async (paymentData) => {
    const response = await fetch(buildApiUrl(`/tenancy-ledgers/${tenancyLedgerId}/payments`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        amount: {
          amount: Math.round(parseFloat(paymentData.amount) * 100),
          currency: agencyCurrency
        },
        payment_date: new Date(paymentData.payment_date).toISOString(),
        reference: paymentData.reference
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create payment');
    }

    return await response.json();
  };

  const updatePayment = async (paymentData) => {
    let intAmount = paymentData.amount;
    if (typeof intAmount === 'string') {
      intAmount = parseFloat(intAmount);
    }
    intAmount = Math.round(intAmount * 100);

    const response = await fetch(buildApiUrl(`/tenancy-ledgers/payments/${paymentData.id}`), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        amount: { amount: intAmount, currency: agencyCurrency },
        payment_date: new Date(paymentData.payment_date).toISOString(),
        reference: paymentData.reference
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update payment');
    }

    return await response.json();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be a positive number.';
    }
    if (!formData.payment_date) {
      newErrors.payment_date = 'Payment date is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSavingPayment(true);
    try {
      const paymentData = { ...formData, amount: parseFloat(formData.amount) };

      let result;
      if (isPaymentSaved && originalPaymentData) {
        const hasChanges = Object.keys(paymentData).some(key => paymentData[key] !== originalPaymentData[key]);
        if (hasChanges) {
          result = await updatePayment(paymentData);
        } else {
          result = { id: formData.id };
        }
      } else {
        result = await createPayment(paymentData);
      }

      const newPaymentId = result.id || result.ID;
      setIsPaymentSaved(true);
      setFormData(prev => ({ ...prev, id: newPaymentId }));

      // Upload any pending files after payment is saved
      if (pendingFiles.length > 0) {
        await uploadAndLinkFiles(newPaymentId, pendingFiles);
        setPendingFiles([]);
      }

      onSave(result);
      onClose();
    } catch (error) {
      console.error('Failed to save payment:', error);
      alert('Failed to save payment. Please try again.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files.map(f => ({ file: f, type: fileTypeSelection }))]);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files.map(f => ({ file: f, type: fileTypeSelection }))]);
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAndLinkFiles = async (paymentId, files) => {
    setUploadingFiles(true);
    try {
      for (const fileObj of files) {
        // Use the combined upload endpoint: POST /payments/{payment_id}/files/upload
        const uploadFormData = new FormData();
        uploadFormData.append('file', fileObj.file);
        uploadFormData.append('file_type', fileObj.type);
        uploadFormData.append('description', fileObj.file.name);

        const response = await fetch(buildApiUrl(`/payments/${paymentId}/files/upload`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
          },
          body: uploadFormData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to upload file: ${fileObj.file.name}`);
        }

        const result = await response.json();
        console.log('File uploaded and linked to payment:', result);
      }

      // Refresh the payment files list
      await fetchPaymentFiles(paymentId);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(`Failed to upload files: ${error.message}`);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleUploadNow = async () => {
    if (pendingFiles.length === 0) return;

    if (isPaymentSaved && formData.id) {
      await uploadAndLinkFiles(formData.id, pendingFiles);
      setPendingFiles([]);
    } else {
      alert('Please save the payment first before uploading files.');
    }
  };

  const handleDeleteFile = async (paymentFile) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(buildApiUrl(`/payment-files/${paymentFile.id}?cleanup=true`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete file');
      }

      // Refresh the files list
      await fetchPaymentFiles(formData.id);

      // Clear preview if deleted file was being previewed
      if (selectedPreviewFile?.id === paymentFile.id) {
        setSelectedPreviewFile(null);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    }
  };

  const getFileTypeLabel = (type) => {
    const labels = {
      receipt: 'Receipt',
      other: 'Other'
    };
    return labels[type] || type;
  };

  if (!show) return null;

  // Check if this is a bond transfer payment (form fields non-editable, but files can be uploaded)
  const isBondTransferPayment = !!(detailedPayment?.BondTransferID || detailedPayment?.bond_transfer_id);

  // Check if payment is verified
  const isVerified = (detailedPayment?.verification_status || detailedPayment?.VerificationStatus) === 'verified';

  // Payment form fields are locked if verified OR if it's a bond transfer
  const isPaymentLocked = isVerified || isBondTransferPayment;

  // File uploads/deletes are only locked if verified (NOT for bond transfers)
  const areFilesLocked = isVerified;

  return (
    <React.Fragment>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[9999]" style={{ backdropFilter: 'blur(2px)' }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden relative flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 flex-shrink-0">
          <h5 className="text-xl font-semibold flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            {isBondTransferPayment ? 'Bond Transfer Payment' : payment ? 'Edit Payment' : 'Add New Payment'}
          </h5>
          <button type="button" className="absolute top-4 right-4 text-emerald-100 hover:text-white transition-colors duration-200" onClick={onClose}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Bond Transfer Info Banner */}
        {isBondTransferPayment && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">This payment was created from a bond transfer and cannot be edited.</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full">
              {/* Left Column - Payment Details */}
              <div className="p-6 space-y-4 border-r border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{currencySymbol}</span>
                      <input
                        type="number"
                        className={`block w-full pl-8 pr-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors duration-200 ${
                          errors.amount ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-emerald-500'
                        } ${isPaymentLocked ? 'bg-gray-50' : ''}`}
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        required
                        placeholder="0.00"
                        disabled={isPaymentLocked}
                      />
                      {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Date *</label>
                    <input
                      type="date"
                      className={`block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors duration-200 ${
                        errors.payment_date ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-emerald-500'
                      } ${isPaymentLocked ? 'bg-gray-50' : ''}`}
                      name="payment_date"
                      value={formData.payment_date}
                      onChange={handleChange}
                      onKeyDown={(e) => e.preventDefault()}
                      required
                      disabled={isPaymentLocked}
                    />
                    {formData.payment_date && (
                      <p className="mt-1 text-xs font-semibold text-blue-600">{formatDateWithContext(formData.payment_date)}</p>
                    )}
                    {errors.payment_date && <p className="mt-1 text-sm text-red-600">{errors.payment_date}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Reference/Transaction ID</label>
                  <input
                    type="text"
                    className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200 ${
                      isPaymentLocked ? 'bg-gray-50' : ''
                    }`}
                    name="reference"
                    value={formData.reference}
                    onChange={handleChange}
                    placeholder="Transaction reference or check number"
                    disabled={isPaymentLocked}
                  />
                </div>

                {/* Linked Transaction Section - Hidden for bond transfer payments */}
                {!isBondTransferPayment && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">Linked Transaction</label>
                      {isPaymentSaved && !isPaymentLocked && (
                        <button
                          type="button"
                          onClick={() => setShowLinkTransactionModal(true)}
                          className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center"
                        >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Link Transaction
                      </button>
                    )}
                  </div>

                  {loadingPaymentDetails ? (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto mb-1"></div>
                      <p className="text-xs text-gray-500">Loading...</p>
                    </div>
                  ) : (detailedPayment?.account_transaction || detailedPayment?.AccountTransaction) ? (
                    <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                      <div className="flex items-center mb-2">
                        <svg className="w-4 h-4 text-green-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-semibold text-green-700">Transaction Linked</span>
                      </div>
                      {(() => {
                        const transaction = detailedPayment.account_transaction || detailedPayment.AccountTransaction;
                        if (transaction) {
                          return (
                            <div className="text-xs text-gray-700 space-y-1">
                              <p><span className="font-medium">Transaction ID:</span> {transaction.id || transaction.ID}</p>
                              <p><span className="font-medium">Date:</span> {formatDateWithContext(transaction.date)}</p>
                              <p className="font-semibold text-green-600">
                                <span className="font-medium text-gray-700">Amount:</span> {currencySymbol}{(transaction.amount / 100).toFixed(2)}
                              </p>
                              {transaction.description && (
                                <p><span className="font-medium">Description:</span> {transaction.description}</p>
                              )}
                            </div>
                          );
                        }
                        return <p className="text-xs text-gray-600">Linked to transaction</p>;
                      })()}
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-sm text-gray-500">No transaction linked</p>
                    </div>
                  )}
                </div>
                )}

                {/* Verification Section - Hidden for bond payments */}
                {!isBondTransferPayment && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Verification Status</label>
                    {loadingPaymentDetails ? (
                      <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto mb-1"></div>
                        <p className="text-xs text-gray-500">Loading...</p>
                      </div>
                    ) : ((detailedPayment?.verification_status || detailedPayment?.VerificationStatus) === 'verified') ? (
                      <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                        <div className="flex items-center mb-2">
                          <svg className="w-4 h-4 text-green-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-semibold text-green-700">Verified</span>
                        </div>
                        {(() => {
                          const verifiedAt = detailedPayment.verified_at || detailedPayment.VerifiedAt;
                          const verifiedByUser = detailedPayment.verified_by_user || detailedPayment.VerifiedByUser;

                          return (
                            <div className="text-xs text-gray-700 space-y-1 ml-5">
                              {verifiedAt && (
                                <p>
                                  <span className="font-medium">Verified on:</span>{' '}
                                  {formatDateWithContext(verifiedAt)}
                                </p>
                              )}
                              {verifiedByUser && (
                                <p>
                                  <span className="font-medium">Verified by:</span>{' '}
                                  {verifiedByUser.given} {verifiedByUser.surname}
                                  {verifiedByUser.email && (
                                    <span className="text-gray-500"> ({verifiedByUser.email})</span>
                                  )}
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ) : isPaymentSaved ? (
                      <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-amber-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-sm font-semibold text-amber-700">Not Verified</span>
                          </div>
                          {hasPermission('payment.verification') && (
                            <button
                              type="button"
                              onClick={handleVerifyPayment}
                              className="px-3 py-1 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Verify Payment
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-amber-600 mt-2">This payment has not been verified yet. Verifying will lock the payment from further editing.</p>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <p className="text-sm text-gray-500">Save payment first to verify</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column - Documents */}
              <div className="flex flex-col h-full">
                {/* Documents Header */}
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h6 className="text-xs font-semibold text-gray-700 flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Documents {paymentFiles.length > 0 && `(${paymentFiles.length})`}
                    </h6>
                    {!areFilesLocked && (
                      <button
                        type="button"
                        onClick={() => setShowUploadSection(!showUploadSection)}
                        className="px-2 py-1 text-xs font-medium text-emerald-700 bg-white border border-emerald-300 rounded hover:bg-emerald-50 transition-colors flex items-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {showUploadSection ? 'Hide' : 'Upload'}
                      </button>
                    )}
                    {areFilesLocked && (
                      <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Locked
                      </span>
                    )}
                  </div>
                </div>

                {/* Upload Section (Collapsible) */}
                {showUploadSection && !areFilesLocked && (
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    {/* File Type Selection */}
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">File Type</label>
                      <select
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                        value={fileTypeSelection}
                        onChange={(e) => setFileTypeSelection(e.target.value)}
                      >
                        <option value="receipt">Receipt</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Drop Zone */}
                    <div
                      className={`border-2 border-dashed rounded p-3 text-center transition-all duration-200 cursor-pointer ${
                        isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-xs text-gray-600">
                        Drop files or <span className="text-emerald-600 font-semibold">browse</span>
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,application/pdf,.doc,.docx"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>

                    {/* Pending Files */}
                    {pendingFiles.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Ready to upload ({pendingFiles.length})</p>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                          {pendingFiles.map((item, index) => (
                            <div key={index} className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs border border-gray-200">
                              <div className="flex items-center min-w-0 flex-1 gap-1">
                                <span className="truncate text-gray-800">{item.file.name}</span>
                                <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs flex-shrink-0">{getFileTypeLabel(item.type)}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removePendingFile(index)}
                                className="ml-1 text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                        {isPaymentSaved && (
                          <button
                            type="button"
                            onClick={handleUploadNow}
                            disabled={uploadingFiles}
                            className="mt-1.5 w-full px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center"
                          >
                            {uploadingFiles ? (
                              <>
                                <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4-4m4 4v12" />
                                </svg>
                                Upload Now
                              </>
                            )}
                          </button>
                        )}
                        {!isPaymentSaved && (
                          <p className="mt-1.5 text-xs text-amber-600 flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Files will upload when payment is saved
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* File List & Preview Area */}
                <div className="flex-1 overflow-y-auto p-3">
                  {loadingFiles ? (
                    <div className="text-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto mb-1"></div>
                      <p className="text-xs text-gray-500">Loading documents...</p>
                    </div>
                  ) : paymentFiles.length > 0 ? (
                    <div className="space-y-2">
                      {/* File List - Compact Table Style */}
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1 px-1">Attached Files</p>
                        <div className="border border-gray-200 rounded">
                          {paymentFiles.map((pf, index) => (
                            <div
                              key={pf.id}
                              className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors ${
                                index !== 0 ? 'border-t border-gray-200' : ''
                              } ${
                                selectedPreviewFile?.id === pf.id 
                                  ? 'bg-emerald-50' 
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                              onClick={() => setSelectedPreviewFile(pf)}
                            >
                              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-xs font-medium text-gray-800 truncate flex-1 min-w-0">
                                {pf.description || 'Document'}
                              </span>
                              <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded flex-shrink-0">
                                {getFileTypeLabel(pf.file_type)}
                              </span>
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {formatDateWithContext(pf.created_at)}
                              </span>
                              {!areFilesLocked && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteFile(pf); }}
                                  className="p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                  title="Delete"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Compact Preview */}
                      {selectedPreviewFile && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-1 px-1">Preview</p>
                          <div className="border border-gray-200 rounded overflow-hidden bg-white">
                            {selectedPreviewFile.medium_url || selectedPreviewFile.thumbnail_url ? (
                              <img
                                src={selectedPreviewFile.medium_url || selectedPreviewFile.thumbnail_url || selectedPreviewFile.original_url}
                                alt={selectedPreviewFile.description || 'File preview'}
                                className="w-full h-auto max-h-80 object-contain bg-gray-50"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center p-6 bg-gray-50">
                                <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                <p className="text-xs text-gray-600 mb-2">{selectedPreviewFile.description || 'Document'}</p>
                                <a
                                  href={selectedPreviewFile.original_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100"
                                >
                                  Open File
                                </a>
                              </div>
                            )}
                            <div className="px-2 py-1.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                              <span className="text-gray-600">{getFileTypeLabel(selectedPreviewFile.file_type)}</span>
                              <span className="text-gray-500">{formatDateWithContext(selectedPreviewFile.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-xs text-gray-500 mb-1">No documents attached</p>
                      {isPaymentSaved && (
                        <button
                          type="button"
                          onClick={() => setShowUploadSection(true)}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          Upload files
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button type="button" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 flex items-center" onClick={onClose}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            {!isPaymentLocked && (
              <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center" disabled={savingPayment}>
                {savingPayment ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {payment ? 'Update' : 'Add'} Payment
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>

    {/* Link Transaction Modal */}
    <LinkTransactionModal
      show={showLinkTransactionModal}
      onClose={() => setShowLinkTransactionModal(false)}
      payment={payment}
      paymentDate={formData.payment_date}
      property={property}
      agencyCurrency={agencyCurrency}
      onTransactionLinked={handleTransactionLinked}
    />
    </React.Fragment>
  );
};

export default PaymentModal;
